import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
  {
    id: 1,
    name: "Priya Sharma",
    role: "Fitness Enthusiast",
    location: "Mumbai, India",
    avatar: "/assets/images/mava-1762174454410.jpg",
    avatarAlt: "Mava logo representing satisfied customer testimonial from fitness enthusiast",
    rating: 5,
    content: `The Mava Steel Heritage bottle has completely transformed my hydration routine. The 24-hour temperature retention is incredible - my morning coffee stays hot until lunch, and my post-workout water stays refreshingly cold. The build quality is exceptional, and I love knowing that every sip contributes to environmental sustainability.`,
    verified: true,
    purchaseDate: "2024-09-15"
  },
  {
    id: 2,
    name: "Arjun Patel",
    role: "Tech Professional",
    location: "Bangalore, India",
    avatar: "/assets/images/mava-1762174454410.jpg",
    avatarAlt: "Mava logo representing satisfied customer testimonial from technology professional",
    rating: 5,
    content: `As someone who spends long hours at the office, the Smart Innovation bottle with app connectivity has been a game-changer. The hydration reminders keep me on track, and the LED temperature display is both functional and futuristic. The sleek design fits perfectly in my car's cup holder and looks great on my desk.`,
    verified: true,
    purchaseDate: "2024-10-02"
  },
  {
    id: 3,
    name: "Meera Krishnan",
    role: "Environmental Activist",
    location: "Chennai, India",
    avatar: "/assets/images/mava-1762174454410.jpg",
    avatarAlt: "Mava logo representing satisfied customer testimonial from environmental activist",
    rating: 5,
    content: `Mava perfectly aligns with my values of sustainable living. The fact that my purchase contributes to tree planting and clean water initiatives makes every sip meaningful. The bottle's durability means I'll never need to buy single-use plastic again. It's not just a product; it's a statement of conscious living.`,
    verified: true,
    purchaseDate: "2024-08-28"
  },
  {
    id: 4,
    name: "Rohit Gupta",
    role: "Marathon Runner",
    location: "Delhi, India",
    avatar: "/assets/images/mava-1762174454410.jpg",
    avatarAlt: "Mava logo representing satisfied customer testimonial from marathon runner",
    rating: 5,
    content: `The Insulated Performance bottle is my training companion. Whether it's a 5K morning run or a full marathon, it keeps my electrolyte drinks at the perfect temperature. The ergonomic grip and leak-proof design mean I can focus on my performance without worrying about spills. Highly recommended for serious athletes.`,
    verified: true,
    purchaseDate: "2024-09-20"
  }];


  const mediaFeatures = [
  {
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_162dbfd7d-1762172116430.png",
    logoAlt: "The Times of India newspaper logo in traditional serif font on white background",
    publication: "The Times of India",
    headline: "Mava Bottles: Redefining Sustainable Hydration",
    date: "October 2024"
  },
  {
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1743ef062-1762172118018.png",
    logoAlt: "Economic Times business newspaper logo with distinctive blue and white branding",
    publication: "Economic Times",
    headline: "Startup Spotlight: Eco-Innovation in Beverage Industry",
    date: "September 2024"
  },
  {
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1181c846b-1762172116904.png",
    logoAlt: "Forbes India magazine logo with iconic red and white design on business publication",
    publication: "Forbes India",
    headline: "Young Entrepreneurs Making Environmental Impact",
    date: "August 2024"
  }];


  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16">

          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary/10 to-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="MessageSquare" size={16} className="text-accent" />
            <span className="text-sm font-medium text-primary">Customer Stories</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-poppins font-bold text-text-primary mb-6">
            What Our{' '}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Community Says
            </span>
          </h2>
          
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Join thousands of satisfied customers who have made Mava their trusted hydration companion.
          </p>
        </motion.div>

        {/* Main Testimonial Display */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white rounded-3xl p-8 lg:p-12 liquid-shadow mb-16">

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTestimonial}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="grid lg:grid-cols-3 gap-8 items-center">

              {/* Customer Info */}
              <div className="text-center lg:text-left">
                <div className="relative inline-block mb-4">
                  <Image
                    src={testimonials?.[activeTestimonial]?.avatar}
                    alt={testimonials?.[activeTestimonial]?.avatarAlt}
                    className="w-24 h-24 rounded-full object-cover mx-auto lg:mx-0" />

                  {testimonials?.[activeTestimonial]?.verified &&
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <Icon name="Check" size={16} color="white" />
                    </div>
                  }
                </div>
                
                <h3 className="font-poppins font-bold text-text-primary text-lg mb-1">
                  {testimonials?.[activeTestimonial]?.name}
                </h3>
                <p className="text-text-secondary text-sm mb-2">
                  {testimonials?.[activeTestimonial]?.role}
                </p>
                <p className="text-text-secondary text-xs mb-4">
                  {testimonials?.[activeTestimonial]?.location}
                </p>
                
                {/* Rating */}
                <div className="flex justify-center lg:justify-start space-x-1 mb-4">
                  {[...Array(testimonials?.[activeTestimonial]?.rating)]?.map((_, i) =>
                  <Icon key={i} name="Star" size={16} className="text-yellow-400 fill-current" />
                  )}
                </div>
                
                <div className="text-xs text-text-secondary">
                  Verified Purchase • {new Date(testimonials[activeTestimonial].purchaseDate)?.toLocaleDateString('en-IN')}
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <Icon name="Quote" size={48} className="text-primary/20 absolute -top-4 -left-4" />
                  <p className="text-lg text-text-primary leading-relaxed relative z-10 pl-8">
                    {testimonials?.[activeTestimonial]?.content}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Dots */}
          <div className="flex justify-center space-x-2 mt-8">
            {testimonials?.map((_, index) =>
            <button
              key={index}
              onClick={() => setActiveTestimonial(index)}
              className={`w-3 h-3 rounded-full brand-transition ${
              activeTestimonial === index ? 'bg-primary' : 'bg-slate-300'}`
              } />

            )}
          </div>
        </motion.div>

        {/* Media Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center">

          <h3 className="text-xl font-poppins font-semibold text-text-primary mb-8">
            Featured In
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {mediaFeatures?.map((feature, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + index * 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-xl liquid-shadow hover:shadow-lg brand-transition">

                <Image
                src={feature?.logo}
                alt={feature?.logoAlt}
                className="h-12 w-auto mx-auto mb-4 object-contain" />

                <h4 className="font-semibold text-text-primary text-sm mb-2">
                  {feature?.headline}
                </h4>
                <p className="text-xs text-text-secondary">
                  {feature?.publication} • {feature?.date}
                </p>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Social Proof Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-16">

          {[
          { value: "50K+", label: "Happy Customers" },
          { value: "4.9/5", label: "Average Rating" },
          { value: "98%", label: "Satisfaction Rate" },
          { value: "24/7", label: "Customer Support" }]?.
          map((stat, index) =>
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1 + index * 0.1, duration: 0.4 }}
            viewport={{ once: true }}
            className="text-center">

              <div className="text-2xl font-bold text-primary mb-1">{stat?.value}</div>
              <div className="text-sm text-text-secondary">{stat?.label}</div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );

};

export default TestimonialsSection;